'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import Cookies from 'js-cookie';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import * as LoginUtils from '../lib/LoginUtils';
import ErrorMessage from './ErrorMessage';
import TokenExpiredAlert from './alerts/TokenExpiredAlert';
import SignupLink from './SignupLink';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIButton from 'UIComponents/button/UIButton';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITextInput from 'UIComponents/input/UITextInput';
import { parse as parseQuery, stringify as stringifyQuery } from 'hub-http/helpers/params';
import IE11Alert from '../components/alerts/IE11Alert';
import selectSessionInvalidationWarning from '../utils/selectSessionInvalidationWarning';
import { ThirdPartyLoginElements } from './ThirdPartyLoginElements';

class LoginForm extends Component {
  constructor(props) {
    super(props);

    this.onClickContinue = () => {
      const {
        loginRedirectUrl
      } = this.props;
      window.location.href = loginRedirectUrl;
    };

    this.handleShowPasswordToggle = () => {
      this.setState({
        showPassword: !this.state.showPassword
      });
    };

    this.handleFocusEmailElement = () => {
      this.setState({
        emailFocused: true,
        loggingInWithGoogle: false
      });
    };

    this.handleBlurEmailElement = () => {
      if (!this.state.loggingInWithGoogle) {
        this.setState({
          emailFocused: false
        });
      }
    };

    this.handleGoogleLoginClick = () => {
      this.setState({
        loggingInWithGoogle: true
      });
      this.props.onGoogleLoginClick(this.triggerReset);
    };

    this.handleInputChange = (name, evt) => {
      this.setState({
        [name]: evt.target.value
      });
    };

    this.isSubmittable = (email, isEmailAutofilled, isPasswordAutoFilled, password) => {
      const emailSatisfied = isEmailAutofilled || !!email && LoginUtils.isValidEmail(email);
      const passwordSatisfied = isPasswordAutoFilled || !!password && password.length >= 6;
      return emailSatisfied && passwordSatisfied;
    };

    this.isValidEmail = email => {
      return !!email && LoginUtils.isValidEmail(email);
    };

    this.onRememberMeChange = ({
      target: {
        checked
      }
    }) => {
      this.setState({
        rememberLogin: checked
      });
    };

    this.onFormSubmit = event => {
      event.preventDefault();
      const {
        onLoginSubmit
      } = this.props;
      const {
        email,
        password,
        rememberLogin
      } = this.state;
      const data = {
        email,
        password,
        rememberLogin
      };
      data.email = LoginUtils.trimEmail(email);
      this.setState({
        submitting: true,
        error: ''
      });
      onLoginSubmit(data, this.triggerReset);
    };

    this.triggerReset = error => {
      const newState = {
        submitting: false,
        error
      };
      this.setState(newState);
    };

    this.renderEmailElement = (email, emailFocused, validEmail, submitting, useDark) => {
      const error = email !== '' && !emailFocused && !validEmail;
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(UIFormControl, {
          htmlFor: "username",
          className: "m-bottom-4",
          label: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "login.form.email"
          }),
          error: error,
          validationMessage: error && /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "login.formValidation.invalidEmail"
          }),
          children: /*#__PURE__*/_jsx(UITextInput, {
            autoFocus: true,
            id: "username",
            onFocus: this.handleFocusEmailElement,
            onBlur: this.handleBlurEmailElement,
            type: "email",
            className: "login-email",
            value: this.state.email,
            onChange: this.handleInputChange.bind(null, 'email'),
            disabled: submitting,
            tabIndex: "1",
            use: useDark === 'true' ? 'on-dark' : 'default'
          })
        })
      });
    };

    this.renderPasswordElement = (submitting, email, useDark, loginRedirectUrl, googleLoginRedirectUrl) => {
      const query = {
        email,
        useDark,
        loginRedirectUrl,
        googleLoginRedirectUrl
      };
      return /*#__PURE__*/_jsx("div", {
        className: "password-input",
        children: /*#__PURE__*/_jsx(UIFormControl, {
          htmlFor: "password",
          label: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "login.form.password"
          }),
          help: /*#__PURE__*/_jsx(UIButton, {
            use: "link",
            onClick: this.handleShowPasswordToggle,
            children: this.state.showPassword ? I18n.text('login.form.hidePassword') : I18n.text('login.form.showPassword')
          }),
          description: /*#__PURE__*/_jsx(Link, {
            to: {
              pathname: '/forgot',
              search: `?${stringifyQuery(query)}`
            },
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "login.form.forgotPassword"
            })
          }),
          children: /*#__PURE__*/_jsx(UITextInput, {
            id: "password",
            type: this.state.showPassword ? 'text' : 'password',
            className: "login-password m-bottom-3",
            value: this.state.password,
            onChange: this.handleInputChange.bind(null, 'password'),
            disabled: submitting,
            tabIndex: "2",
            use: useDark === 'true' ? 'on-dark' : 'default'
          })
        })
      });
    };

    this.renderRememberMeElement = (submitting, rememberLogin) => {
      return /*#__PURE__*/_jsx(UICheckbox, {
        "data-test-id": "remember-me",
        id: "remember",
        className: "m-bottom-4",
        checked: rememberLogin,
        onChange: this.onRememberMeChange,
        disabled: submitting,
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "login.form.remember"
        })
      });
    };

    this.renderSubmitButtonElement = (submittable, submitting) => {
      if (submitting) {
        return /*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true,
          minHeight: "56px"
        });
      } else {
        return /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "password-login-button",
          id: "loginBtn",
          type: "submit",
          use: "primary",
          className: "m-bottom-4 login-submit",
          disabled: !submittable,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "login.form.button"
          })
        });
      }
    };

    this.renderSsoLoginButton = () => {
      const {
        email
      } = this.state;
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "sso-login-button",
        id: "ssoBtn",
        type: "button",
        use: "tertiary",
        className: "m-top-4 width-100",
        onClick: () => this.props.onSsoLoginClick(email),
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "login.form.sso.loginButton"
        })
      });
    };

    const {
      search
    } = props.location;

    const _query = parseQuery(search.substr(1));

    const _rememberLogin = Cookies.get('prefs') === '1';

    this.state = {
      email: _query['email'] || '',
      emailFocused: false,
      loggingInWithGoogle: false,
      error: '',
      isEmailAutofilled: false,
      isPasswordAutoFilled: false,
      password: '',
      queryParamError: _query['loginError'] || '',
      showPassword: false,
      submitting: false,
      rememberLogin: _rememberLogin
    };
  }

  componentDidMount() {
    setTimeout(() => {
      try {
        this.setState({
          isEmailAutofilled: document.querySelectorAll('#username:-webkit-autofill').length > 0,
          isPasswordAutoFilled: document.querySelectorAll('#password:-webkit-autofill').length > 0
        }); // eslint-disable-next-line no-empty
      } catch (ex) {}
    }, 500); // This allows automation to hook into the page for easy login. (For example speedcurve)

    window._hubspotAutomatedLoginHook = this.handleInputChange;
  }

  render() {
    const {
      email,
      emailFocused,
      password,
      error,
      isEmailAutofilled,
      isPasswordAutoFilled,
      queryParamError,
      submitting,
      rememberLogin
    } = this.state;
    const submittable = !submitting && this.isSubmittable(email, isEmailAutofilled, isPasswordAutoFilled, password);
    const validEmail = this.isValidEmail(email);
    const {
      location,
      signupParams,
      isInvalidTokenAlertVisible
    } = this.props;
    const query = parseQuery(location.search.substr(1));
    const {
      useDark,
      loginRedirectUrl,
      googleLoginRedirectUrl,
      allowFbSignup,
      hideSignupLink,
      authFailureReason
    } = query;
    const updatedError = isInvalidTokenAlertVisible ? 'LOGIN_UNAVAILABLE' : error;
    const sessionInvalidationWarning = selectSessionInvalidationWarning(authFailureReason);
    return /*#__PURE__*/_jsxs("form", {
      id: "hs-login",
      onSubmit: this.onFormSubmit,
      children: [!hideSignupLink && /*#__PURE__*/_jsx(SignupLink, {
        isFbSignin: allowFbSignup,
        signupParams: signupParams,
        loginRedirectUrlQueryParam: loginRedirectUrl
      }), /*#__PURE__*/_jsx(IE11Alert, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        error: queryParamError || sessionInvalidationWarning,
        type: queryParamError ? 'danger' : 'info',
        prefix: queryParamError ? 'login.errors' : 'login.warnings'
      }), this.renderEmailElement(email, emailFocused, validEmail, submitting, useDark), this.renderPasswordElement(submitting, email, useDark, loginRedirectUrl, googleLoginRedirectUrl), this.renderRememberMeElement(submitting, rememberLogin), /*#__PURE__*/_jsx(ErrorMessage, {
        error: updatedError
      }), /*#__PURE__*/_jsx(TokenExpiredAlert, {}), this.renderSubmitButtonElement(submittable, submitting), /*#__PURE__*/_jsx("hr", {}), /*#__PURE__*/_jsx(ThirdPartyLoginElements, {
        onGoogleLoginClick: this.handleGoogleLoginClick
      }), this.renderSsoLoginButton()]
    });
  }

}

LoginForm.propTypes = {
  location: PropTypes.object.isRequired,
  onLoginSubmit: PropTypes.func.isRequired,
  onGoogleLoginClick: PropTypes.func.isRequired,
  onSsoLoginClick: PropTypes.func.isRequired,
  isInvalidTokenAlertVisible: PropTypes.bool,
  loginRedirectUrl: PropTypes.string,
  signupParams: PropTypes.string
};
export default withRouter(LoginForm);